import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BusinessEmailAddressInput from '../form-inputs/business-email-address-input/BusinessEmailAddressInput';
import RegisteredAddressInput from '../form-inputs/registered-address-input/RegisteredAddressInput';
import PhysicalAddressInput from '../form-inputs/physical-address-input/PhysicalAddressInput';
import BusinessPhoneNumberInput from '../form-inputs/business-phone-number-input/BusinessPhoneNumberInput';
import PostalAddressInput from '../form-inputs/postal-address-input/PostalAddressInput';
import RegistrationNumberInput from '../form-inputs/registration-number-input/RegistrationNumberInput';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import OrganizationIcon from '../../../assets/svg/organization.svg?react';
import CountrySelector from '../form-inputs/country-selector/CountrySelector';
import PhoneNumberInput from '../form-inputs/phone-number-input/PhoneNumberInput';

import { selectBusinessProfileStatus } from '../../slices/business-profile/businessProfileSlice';
import CircularProgress from '@mui/material/CircularProgress';
import { useDetailsCardForm } from './useDetailsCardForm';
import styles from './DetailsCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import TaxRegistrationNumberInput from '../form-inputs/registration-number-input/TaxRegistrationNumberInput';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { useAppSelector } from '../hooks/useAppSelector';
import { Company } from '../../models/company.model';
//import WhatsAppOptInCheckbox from '../whatsapp-opt-in-checkbox/WhatsAppOptInCheckbox';

type DetailsCardProps = {
  simplified?: boolean;
  company: Company;
};

export default function DetailsCard({ simplified, company }: DetailsCardProps) {
  const { t } = useTranslation();
  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);
  const isLoading = businessProfileStatus === 'loading';

  // const socialLinks: SocialLink[] = [
  //   // TODO: this will be replaced by endpoint response
  //   { key: 'website', name: 'website', url: 'https://company.com', icon: <WebIcon /> },
  //   { key: 'facebook', name: 'facebook', url: 'https://facebook.com', icon: <InstagramIcon /> },
  //   { key: 'twitter', name: 'twitter', url: 'https://twitter.com', icon: <InstagramIcon /> },
  //   { key: 'instagram', name: 'instagram', url: 'https://instagram.com', icon: <InstagramIcon /> },
  //   { key: 'linkedin', name: 'linkedin', url: 'https://linkedin.com', icon: <InstagramIcon /> },
  //   { key: 'youtube', name: 'youtube', url: 'https://youtube.com', icon: <InstagramIcon /> },
  // ];

  /*const getBusinesProfileDetails = useCallback(() => {
        if (!businessProfileDetails && company?.id) {
          dispatch(fetchBusinessProfileById({ id: company.id }));
        }
      }, [dispatch, company?.id, businessProfileDetails]);
    
      useEffect(() => {
        getBusinesProfileDetails();
      }, [getBusinesProfileDetails]);*/

  const {
    formData,
    handleChange,
    companyName,
    handleCompanyNameChange,
    //handleCheckboxChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    country,
    setCountry,
    companyNameValidated,
    cancelChanges,
  } = useDetailsCardForm(company.id!);

  return (
    <Grid component='div' container className='inputs-editable'>
      <Grid component='div' size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent
            className={isLoading ? styles.card_content_min_height : ''}
            sx={{ paddingTop: 0, paddingBottom: '0 !important' }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid
                  component='div'
                  container
                  style={{
                    alignItems: 'center',
                    minHeight: '76px',
                    paddingTop: '16px !important',
                    paddingBottom: '16px !important',
                    paddingLeft: '0 !important',
                    paddingRight: '0 !important',
                    flexWrap: 'nowrap',
                    padding: '8px',
                  }}
                >
                  <Grid
                    component='div'
                    size={{ xs: 12 }}
                    container
                    style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                  >
                    <Grid
                      component='div'
                      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                      <OrganizationIcon />
                    </Grid>
                    <Grid component='div'>
                      <Typography variant='h6' className='semibold-variant'>
                        {t('businessProfile.detailsCardHeader')}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    component='div'
                    size={{ xs: 1 }}
                    container
                    style={{ justifyContent: 'flex-end' }}
                  >
                    {!isEdited ? (
                      <Tooltip title={t('buttons.edit')} placement='top'>
                        <IconButton
                          id='detailsCardEditIconBtn'
                          onClick={toggleEditing}
                          sx={{ flexShrink: 0 }}
                        >
                          <BrushIcon id='detailsCardEditIconBtnBrushIcon' />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                          id='detailsCardCancelBtn'
                          type='button'
                          className='btn-default btn-action-basic btn-cancel'
                          role='button'
                          variant='contained'
                          size='large'
                          onClick={cancelChanges}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          id='detailsCardSaveBtn'
                          type='submit'
                          className='btn-default btn-action-basic'
                          role='button'
                          disabled={
                            !isEdited ||
                            !companyName ||
                            !companyNameValidated ||
                            !formData.business_email_address
                          }
                          variant='contained'
                          size='large'
                          onClick={saveChanges}
                        >
                          {t('buttons.save')}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                {!simplified ? (
                  <Grid component='div' container spacing={2}>
                    <Grid component='div' size={{ xs: 12, sm: 6 }}>
                      <Grid component='div' container spacing={2}>
                        <Grid component='div' size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            value={companyName}
                            required={true}
                            error={!companyNameValidated}
                            label={t('formInputs.businessNameInputLabel')}
                            onChange={(e) => {
                              handleCompanyNameChange(e.target.value);
                            }}
                            helperText={
                              !companyNameValidated
                                ? t('messages.businessNameValidationMessage')
                                : null
                            }
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12 }}>
                          <PhoneNumberInput
                            value={formData.business_phone_number}
                            onChange={handleChange('business_phone_number')}
                            disabled={!isEdited}
                          />
                        </Grid>
                        {/*<Grid component='div' size={{ xs: 12 }}>
                          <WhatsAppOptInCheckbox
                            value={formData.marketing_opt_in ?? false}
                            onChange={handleCheckboxChange('marketing_opt_in')}
                            disabled={!isEdited}
                          />
                        </Grid>*/}
                        <Grid component='div' size={{ xs: 12 }}>
                          <BusinessEmailAddressInput
                            value={formData.business_email_address ?? ''}
                            required={true}
                            onChange={handleChange('business_email_address')}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12 }}>
                          <CountrySelector
                            value={country}
                            setCountry={setCountry}
                            disabled={!isEdited}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid component='div' size={{ xs: 12, sm: 6 }}>
                      <Grid component='div' container spacing={2}>
                        <Grid component='div' size={{ xs: 12 }}>
                          <Grid component='div' container spacing={2}>
                            <Grid component='div' size={{ xs: 12, sm: 6 }}>
                              <RegistrationNumberInput
                                value={formData.registration_number}
                                onChange={handleChange('registration_number')}
                                disabled={!isEdited}
                              />
                            </Grid>
                            <Grid component='div' size={{ xs: 12, sm: 6 }}>
                              {' '}
                              <TaxRegistrationNumberInput
                                value={formData.registration_number_2_or_tax_number}
                                onChange={handleChange('registration_number_2_or_tax_number')}
                                disabled={!isEdited}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid component='div' size={{ xs: 12 }}>
                          <RegisteredAddressInput
                            value={formData.registered_address}
                            onChange={handleChange('registered_address')}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12 }}>
                          <PhysicalAddressInput
                            value={formData.physical_address}
                            onChange={handleChange('physical_address')}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12 }}>
                          <PostalAddressInput
                            value={formData.postal_address}
                            onChange={handleChange('postal_address')}
                            disabled={!isEdited}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid component='div' container spacing={2}>
                    <Grid component='div' size={{ xs: 12, sm: 6, md: 6 }}>
                      <TextField
                        fullWidth
                        value={companyName}
                        label={t('formInputs.businessNameInputLabel')}
                        onChange={(e) => {
                          handleCompanyNameChange(e.target.value);
                        }}
                        disabled={!isEdited}
                      />
                    </Grid>
                    <Grid component='div' size={{ xs: 12, sm: 6, md: 6 }}>
                      <BusinessEmailAddressInput
                        required={true}
                        value={formData.business_email_address ?? ''}
                        onChange={handleChange('business_email_address')}
                        disabled={!isEdited}
                      />
                    </Grid>

                    <Grid component='div' size={{ xs: 12, sm: 6, md: 6 }}>
                      <BusinessPhoneNumberInput
                        value={formData.business_phone_number}
                        onChange={handleChange('business_phone_number')}
                        disabled={!isEdited}
                      />
                    </Grid>
                    <Grid component='div' size={{ xs: 12, sm: 6, md: 6 }}>
                      <Grid component='div' container spacing={2}>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          {' '}
                          <RegistrationNumberInput
                            value={formData.registration_number}
                            onChange={handleChange('registration_number')}
                            disabled={!isEdited}
                          />
                        </Grid>
                        <Grid component='div' size={{ xs: 12, sm: 6 }}>
                          <TaxRegistrationNumberInput
                            value={formData.registration_number_2_or_tax_number}
                            onChange={handleChange('registration_number_2_or_tax_number')}
                            disabled={!isEdited}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {
                  <Grid
                    component='div'
                    container
                    className={styles.container_margin_top}
                    style={{ alignItems: 'center' }}
                  >
                    <Grid component='div'>
                      {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Grid>
                  </Grid>
                }
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
      {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
    </Grid>
  );
}
